import type { ProductCatalogState } from "@adpilot/shared";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { productCatalogBlocksCompletion } from "./product-listing-helpers";
import type { ProductCatalogSync } from "./use-product-catalog-sync";

interface CatalogStatusAlertsProps {
  catalog: ProductCatalogState | undefined;
  sync: ProductCatalogSync;
  allowCatalogRefresh: boolean;
  showInventoryRefresh: boolean;
  onReconnect: () => void;
}

/** Catalog completeness, inventory freshness and manual catalog refresh controls. */
export function CatalogStatusAlerts({
  catalog,
  sync,
  allowCatalogRefresh,
  showInventoryRefresh,
  onReconnect,
}: CatalogStatusAlertsProps) {
  const { t } = useTranslation("campaignCreation");
  const {
    refresh,
    refreshStatus,
    refreshRunId,
    refreshFailure,
    refreshPartial,
    refreshSkipped,
    refreshPending,
  } = sync;
  const inventoryNotice =
    showInventoryRefresh && catalog?.inventory?.status !== "fresh" ? catalog?.inventory : undefined;
  const catalogBlocksCompletion = productCatalogBlocksCompletion(catalog);
  const showCatalogAlert = Boolean(
    catalog &&
      (catalogBlocksCompletion ||
        (allowCatalogRefresh && (catalog.completeness !== "complete" || catalog.isStale))),
  );
  // Vendors have no Seller Central to connect: their Ads metadata catalog,
  // keyed by ASIN, is the whole catalog (#673), not something to complete.
  const adsOnlyCatalog = catalog?.source === "ads-metadata" && catalog.accountType === "seller";
  let catalogTitle = catalog ? t(`products.catalog.${catalog.completeness}.title`) : "";
  let catalogDescription = catalog ? t(`products.catalog.${catalog.completeness}.description`) : "";
  if (catalog?.completeness === "complete" && !catalog.isStale) {
    catalogDescription = t("products.quiet.current");
  }
  if (refreshPending) {
    catalogTitle = t("products.quiet.preparing");
    catalogDescription = t(
      refreshStatus.isError ? "products.refreshStatusUnavailable" : "products.refreshPending",
    );
  }
  if (refreshPartial || refreshSkipped) {
    catalogTitle = t("products.quiet.updatingTitle");
    catalogDescription = t("products.quiet.updatingTitle");
  }
  if (refreshFailure) {
    catalogTitle = t("products.refreshFailed");
    catalogDescription = refreshFailure;
  }
  return (
    <>
      {adsOnlyCatalog ? (
        <Alert>
          <IconAlertTriangle />
          <AlertTitle>{t("products.catalog.adsOnly.title")}</AlertTitle>
          <AlertDescription>{t("products.catalog.adsOnly.description")}</AlertDescription>
          <AlertAction>
            <Button
              variant="outline"
              size="sm"
              render={<Link to="/settings" search={{ tab: "amazon" }} />}
            >
              {t("products.catalog.adsOnly.connect")}
            </Button>
          </AlertAction>
        </Alert>
      ) : null}
      {catalog && showCatalogAlert ? (
        <Alert
          variant={catalog.completeness === "unavailable" || refreshFailure ? "error" : "default"}
        >
          <IconAlertTriangle />
          <AlertTitle>{catalogTitle}</AlertTitle>
          {catalogDescription !== catalogTitle || catalogBlocksCompletion ? (
            <AlertDescription className="flex items-center justify-between gap-3">
              {catalogDescription !== catalogTitle ? <span>{catalogDescription}</span> : null}
              <span className="flex shrink-0 gap-2">
                {catalogBlocksCompletion ? (
                  <Button type="button" variant="outline" size="sm" onClick={onReconnect}>
                    {t("profile.empty.action")}
                  </Button>
                ) : null}
              </span>
            </AlertDescription>
          ) : null}
        </Alert>
      ) : null}

      {(refreshPartial || refreshSkipped) && !showCatalogAlert ? (
        <Alert role="status">
          <IconAlertTriangle />
          <AlertTitle>{t("products.quiet.updatingTitle")}</AlertTitle>
        </Alert>
      ) : null}

      {inventoryNotice ? (
        <Alert
          variant={inventoryNotice.status === "failed" ? "error" : "default"}
          aria-busy={sync.manualRefreshStarted}
        >
          <IconAlertTriangle />
          <AlertTitle>{t(`products.inventoryNotice.${inventoryNotice.status}`)}</AlertTitle>
          <AlertDescription>
            <p>{t("products.inventoryNotice.description")}</p>
          </AlertDescription>
        </Alert>
      ) : null}

      {allowCatalogRefresh ? (
        <details className="text-xs">
          <summary className="cursor-pointer rounded-md px-2 py-1.5 text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {t("products.quiet.details")}
          </summary>
          <div className="flex flex-col gap-2.5 p-4">
            <p role="status" className="leading-[18px] text-muted-foreground">
              {catalogDescription}
            </p>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={refreshPending}
                onClick={() => refresh.mutate()}
              >
                {refreshPending ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <IconRefresh data-icon="inline-start" />
                )}
                {t("products.refresh")}
              </Button>
              {refreshRunId && refreshStatus.isError ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void refreshStatus.refetch()}
                >
                  {t("actions.retry")}
                </Button>
              ) : null}
            </div>
          </div>
        </details>
      ) : null}
    </>
  );
}
